import { useState } from "react";
import { ArrowLeft, TrendingUp, TrendingDown, Activity, DollarSign, BarChart3, Newspaper, Briefcase, ChevronRight, Gauge, LineChart as LineChartIcon, ShieldCheck, Grid3X3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { SimulationState, SimStock, NewsItem } from "@/hooks/useSimulator";
import TradeModal from "./TradeModal";
import StockChart from "./StockChart";
import SectorHeatmap from "./SectorHeatmap";
import EconomicIndicators from "./EconomicIndicators";
import PortfolioRiskAnalyzer from "./PortfolioRiskAnalyzer";
import TrendDetector from "./TrendDetector";
import type { AnyRow } from "@/lib/rowTypes";

const CYCLE_LABELS: Record<string, { label: string; color: string }> = {
  expansion: { label: "Expansion", color: "text-emerald-400" },
  peak: { label: "Peak", color: "text-amber-400" },
  recession: { label: "Recession", color: "text-red-400" },
  recovery: { label: "Recovery", color: "text-blue-400" },
};

const SENTIMENT_LABELS = [
  { max: 15, label: "Extreme Fear", color: "text-red-500" },
  { max: 35, label: "Fear", color: "text-red-400" },
  { max: 55, label: "Neutral", color: "text-muted-foreground" },
  { max: 75, label: "Greed", color: "text-emerald-400" },
  { max: 100, label: "Euphoria", color: "text-amber-400" },
];

function getSentimentLabel(val: number) {
  return SENTIMENT_LABELS.find((s) => val <= s.max) || SENTIMENT_LABELS[4];
}

const SECTOR_ICONS: Record<string, string> = {
  Technology: "💻",
  Energy: "⚡",
  Consumer: "🛒",
  Finance: "🏦",
  Industrial: "🏭",
};

interface Props {
  simulation: SimulationState;
  stocks: SimStock[];
  portfolioValue: number;
  netWorth: number;
  currentNews: NewsItem[];
  onBuy: (ticker: string, shares: number) => Promise<void>;
  onSell: (ticker: string, shares: number) => Promise<void>;
  onAdvance: () => Promise<void>;
  actionLoading: boolean;
  error: string | null;
  onBack: () => void;
}

const SimDashboard = ({
  simulation: sim,
  stocks,
  portfolioValue,
  netWorth,
  currentNews,
  onBuy,
  onSell,
  onAdvance,
  actionLoading,
  error,
  onBack,
}: Props) => {
  const [tradeStock, setTradeStock] = useState<string | null>(null);
  const [chartStock, setChartStock] = useState<string | null>(null);
  const [tab, setTab] = useState<"market" | "portfolio" | "news" | "analysis">("market");
  const [analysisView, setAnalysisView] = useState<"heatmap" | "indicators" | "risk" | "trends">("heatmap");

  const cycle = CYCLE_LABELS[sim.market_cycle] || CYCLE_LABELS.expansion;
  const sentimentInfo = getSentimentLabel(sim.sentiment);

  // Group stocks by sector
  const sectors = stocks.reduce<Record<string, SimStock[]>>((acc, s) => {
    (acc[s.sector] = acc[s.sector] || []).push(s);
    return acc;
  }, {});

  // Price change from last round
  const prevPrices =
    sim.price_history.length >= 2
      ? sim.price_history[sim.price_history.length - 2]?.prices || {}
      : sim.price_history[0]?.prices || {};

  const hw500Prev =
    sim.hw500_history.length >= 2
      ? sim.hw500_history[sim.hw500_history.length - 2]?.level
      : sim.hw500_history[0]?.level || sim.hw500_level;

  const hw500Change = hw500Prev ? ((sim.hw500_level - hw500Prev) / hw500Prev) * 100 : 0;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/80 backdrop-blur-md sticky top-0 z-50">
        <div className="container max-w-5xl mx-auto flex items-center justify-between py-3 px-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={onBack} className="text-muted-foreground hover:text-foreground hover:bg-muted/50">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="font-heading font-bold text-sm text-foreground">Investing Fundamentals</h1>
              <p className="text-muted-foreground text-xs">
                Round {sim.current_round}/{sim.max_rounds} · {sim.persona.charAt(0).toUpperCase() + sim.persona.slice(1)}
              </p>
            </div>
          </div>
          <Button
            onClick={onAdvance}
            disabled={actionLoading}
            className="bg-accent/20 border border-accent/50 text-accent hover:bg-accent/30 font-heading text-xs shadow-[0_0_15px_hsl(var(--accent)/0.2)]"
          >
            {actionLoading ? "..." : `Advance Round →`}
          </Button>
        </div>
      </header>

      {/* Stats Strip */}
      <div className="border-b border-border/50 bg-card/60">
        <div className="container max-w-5xl mx-auto flex items-center gap-4 py-2 px-4 overflow-x-auto scrollbar-hide text-xs min-w-0">
          <div className="shrink-0">
            <span className="text-muted-foreground">Net Worth</span>
            <p className="text-foreground font-heading font-bold">${netWorth.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
          </div>
          <div className="w-px h-6 bg-border shrink-0" />
          <div className="shrink-0">
            <span className="text-muted-foreground">Cash</span>
            <p className="text-emerald-400 font-heading font-bold">${Number(sim.cash_balance).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
          </div>
          <div className="w-px h-6 bg-border shrink-0" />
          <div className="shrink-0">
            <span className="text-muted-foreground">Portfolio</span>
            <p className="text-accent font-heading font-bold">${portfolioValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
          </div>
          <div className="w-px h-6 bg-border shrink-0" />
          <div className="shrink-0">
            <span className="text-muted-foreground">HW500</span>
            <p className={`font-heading font-bold ${hw500Change >= 0 ? "text-emerald-400" : "text-red-400"}`}>
              {sim.hw500_level.toFixed(1)} <span className="text-[10px]">{hw500Change >= 0 ? "+" : ""}{hw500Change.toFixed(2)}%</span>
            </p>
          </div>
          <div className="w-px h-6 bg-border shrink-0" />
          <div className="shrink-0">
            <span className="text-muted-foreground">Cycle</span>
            <p className={`font-heading font-bold ${cycle.color}`}>{cycle.label}</p>
          </div>
          <div className="w-px h-6 bg-border shrink-0" />
          <div className="shrink-0">
            <span className="text-muted-foreground">Sentiment</span>
            <p className={`font-heading font-bold ${sentimentInfo.color}`}>{sentimentInfo.label}</p>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="container max-w-5xl mx-auto px-4 pt-2">
          <div className="bg-red-400/10 border border-red-400/30 rounded-lg px-3 py-2 text-red-400 text-xs">{error}</div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="container max-w-5xl mx-auto px-4 pt-4">
        <div className="flex gap-1 bg-muted/30 rounded-lg p-1 w-fit overflow-x-auto">
          {(["market", "portfolio", "news", "analysis"] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-3 py-1.5 rounded-md text-xs font-heading font-semibold transition-all whitespace-nowrap ${
                tab === t ? "bg-accent/20 text-accent" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {t === "market" ? "Market" : t === "portfolio" ? "Portfolio" : t === "news" ? "News" : "📊 Analysis"}
            </button>
          ))}
        </div>
      </div>

      <main className="container max-w-5xl mx-auto py-4 px-4">
        {/* Market Tab */}
        {tab === "market" && (
          <div className="space-y-4">
            {Object.entries(sectors).map(([sector, sectorStocks]) => (
              <div key={sector}>
                <h3 className="font-heading font-bold text-sm text-foreground/80 mb-2">
                  {SECTOR_ICONS[sector] || "📈"} {sector}
                </h3>
                <div className="space-y-1">
                  {sectorStocks.map((stock) => {
                    const price = sim.stock_prices[stock.ticker] || stock.current_price;
                    const prev = prevPrices[stock.ticker] || price;
                    const change = ((price - prev) / prev) * 100;
                    const holding = sim.holdings[stock.ticker];

                    return (
                      <div
                        key={stock.ticker}
                        className="flex items-center justify-between p-3 rounded-xl border border-border/50 bg-muted/20 hover:bg-muted/40 transition-all group"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-heading font-bold text-sm text-accent/80">{stock.ticker}</span>
                            <span className="text-muted-foreground text-xs truncate">{stock.company_name}</span>
                            {holding && (
                              <span className="text-[10px] bg-accent/10 text-accent px-1.5 py-0.5 rounded-full">
                                {holding.shares} shares
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <p className="text-foreground font-heading font-bold text-sm">${price.toFixed(2)}</p>
                            <p className={`text-xs font-medium ${change >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                              {change >= 0 ? "+" : ""}{change.toFixed(2)}%
                            </p>
                          </div>
                          <div className="flex gap-1 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => setChartStock(stock.ticker)}
                              className="p-1.5 rounded-lg border border-border hover:bg-muted/50 text-muted-foreground hover:text-foreground"
                            >
                              <BarChart3 className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => setTradeStock(stock.ticker)}
                              className="p-1.5 rounded-lg border border-accent/30 hover:bg-accent/10 text-accent/70 hover:text-accent"
                            >
                              <DollarSign className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Portfolio Tab */}
        {tab === "portfolio" && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="p-4 rounded-xl border border-border bg-muted/30">
                <p className="text-muted-foreground text-xs">Cash Balance</p>
                <p className="text-emerald-400 font-heading font-bold text-lg">${Number(sim.cash_balance).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
              </div>
              <div className="p-4 rounded-xl border border-border bg-muted/30">
                <p className="text-muted-foreground text-xs">Portfolio Value</p>
                <p className="text-accent font-heading font-bold text-lg">${portfolioValue.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
              </div>
            </div>

            {Object.keys(sim.holdings).length === 0 ? (
              <div className="text-center py-12">
                <Briefcase className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-muted-foreground text-sm">No holdings yet. Buy stocks from the Market tab.</p>
              </div>
            ) : (
              <div className="space-y-1">
                <h3 className="font-heading font-bold text-sm text-foreground/80 mb-2">Holdings</h3>
                {Object.entries(sim.holdings).map(([ticker, holding]) => {
                  const price = sim.stock_prices[ticker] || 0;
                  const value = price * holding.shares;
                  const gain = ((price - holding.avgCost) / holding.avgCost) * 100;
                  const stock = stocks.find((s) => s.ticker === ticker);

                  return (
                    <div key={ticker} className="flex items-center justify-between p-3 rounded-xl border border-border/50 bg-muted/20">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-heading font-bold text-sm text-accent/80">{ticker}</span>
                          <span className="text-muted-foreground text-xs">{stock?.company_name}</span>
                        </div>
                        <p className="text-muted-foreground text-xs">{holding.shares} shares · avg ${holding.avgCost.toFixed(2)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-foreground font-heading font-bold text-sm">${value.toFixed(2)}</p>
                        <p className={`text-xs font-medium ${gain >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                          {gain >= 0 ? "+" : ""}{gain.toFixed(2)}%
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Sector allocation */}
            {Object.keys(sim.holdings).length > 0 && (
              <div>
                <h3 className="font-heading font-bold text-sm text-foreground/80 mb-2">Sector Allocation</h3>
                <div className="space-y-2">
                  {(() => {
                    const sectorValues: Record<string, number> = {};
                    for (const [ticker, h] of Object.entries(sim.holdings)) {
                      const stock = stocks.find((s) => s.ticker === ticker);
                      const val = (sim.stock_prices[ticker] || 0) * h.shares;
                      if (stock) sectorValues[stock.sector] = (sectorValues[stock.sector] || 0) + val;
                    }
                    const total = Object.values(sectorValues).reduce((s, v) => s + v, 0);
                    return Object.entries(sectorValues)
                      .sort((a, b) => b[1] - a[1])
                      .map(([sector, val]) => (
                        <div key={sector} className="flex items-center gap-3">
                          <span className="text-muted-foreground text-xs w-24">{SECTOR_ICONS[sector]} {sector}</span>
                          <div className="flex-1 h-2 rounded-full bg-muted/50 overflow-hidden">
                            <div
                              className="h-full bg-accent/60 rounded-full transition-all"
                              style={{ width: `${(val / total) * 100}%` }}
                            />
                          </div>
                          <span className="text-muted-foreground text-xs w-12 text-right">{((val / total) * 100).toFixed(0)}%</span>
                        </div>
                      ));
                  })()}
                </div>
              </div>
            )}
          </div>
        )}

        {/* News Tab */}
        {tab === "news" && (
          <div className="space-y-3">
            {currentNews.length === 0 && sim.current_round === 0 ? (
              <div className="text-center py-12">
                <Newspaper className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-muted-foreground text-sm">Advance a round to see market news.</p>
              </div>
            ) : (
              <>
                {[...(sim.news_log || [])].reverse().map((roundNews: AnyRow, ri) => (
                  <div key={ri}>
                    <p className="text-muted-foreground/50 text-xs font-heading mb-1">Round {roundNews.round}</p>
                    {roundNews.news.map((n: NewsItem, ni: number) => (
                      <div key={ni} className="flex items-start gap-3 p-3 rounded-xl border border-border/50 bg-muted/20 mb-1">
                        <span className={`text-lg ${n.sentiment === "positive" ? "text-emerald-400" : "text-red-400"}`}>
                          {n.sentiment === "positive" ? "📈" : "📉"}
                        </span>
                        <div className="flex-1">
                          <p className="text-foreground/90 text-sm font-medium">{n.headline}</p>
                          <div className="flex gap-2 mt-1">
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted/30 text-muted-foreground">{n.tag}</span>
                            {n.sector && <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted/30 text-muted-foreground">{n.sector}</span>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        {/* Analysis Tab */}
        {tab === "analysis" && (
          <div className="space-y-4">
            {/* Analysis sub-navigation */}
            <div className="flex gap-1 overflow-x-auto">
              {([
                { key: "heatmap", label: "Heatmap", icon: "🗺️" },
                { key: "indicators", label: "Economy", icon: "📊" },
                { key: "risk", label: "Risk", icon: "🛡️" },
                { key: "trends", label: "Trends", icon: "📈" },
              ] as const).map((item) => (
                <button
                  key={item.key}
                  onClick={() => setAnalysisView(item.key)}
                  className={`px-3 py-1.5 rounded-lg text-[11px] font-heading font-semibold transition-all whitespace-nowrap border ${
                    analysisView === item.key
                      ? "bg-accent/15 border-accent/30 text-accent"
                      : "border-border/50 text-muted-foreground hover:text-foreground hover:bg-muted/30"
                  }`}
                >
                  {item.icon} {item.label}
                </button>
              ))}
            </div>

            {analysisView === "heatmap" && <SectorHeatmap stocks={stocks} simulation={sim} />}
            {analysisView === "indicators" && <EconomicIndicators simulation={sim} />}
            {analysisView === "risk" && (
              <PortfolioRiskAnalyzer
                simulation={sim}
                stocks={stocks}
                portfolioValue={portfolioValue}
                netWorth={netWorth}
              />
            )}
            {analysisView === "trends" && <TrendDetector stocks={stocks} simulation={sim} />}
          </div>
        )}
      </main>

      {/* Trade Modal */}
      {tradeStock && stocks.find((s) => s.ticker === tradeStock) && (
        <TradeModal
          ticker={tradeStock}
          stock={stocks.find((s) => s.ticker === tradeStock)!}
          price={sim.stock_prices[tradeStock] || 0}
          cash={Number(sim.cash_balance)}
          holding={sim.holdings[tradeStock]}
          onBuy={onBuy}
          onSell={onSell}
          onClose={() => setTradeStock(null)}
        />
      )}

      {/* Chart Modal */}
      {chartStock && stocks.find((s) => s.ticker === chartStock) && (
        <StockChart
          ticker={chartStock}
          stock={stocks.find((s) => s.ticker === chartStock)!}
          priceHistory={sim.price_history}
          hw500History={sim.hw500_history}
          onClose={() => setChartStock(null)}
        />
      )}
    </div>
  );
};

export default SimDashboard;
